import { trigger, state, style, transition, animate } from '@angular/animations';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HttpRequest, HttpResponse, HttpErrorResponse, HTTP_INTERCEPTORS, HttpParams, HttpHeaders } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject, Component, Input, Pipe, ErrorHandler, APP_INITIALIZER, NgModule, InjectionToken, Output, HostBinding } from '@angular/core';
import * as i1$2 from '@angular/platform-browser';
import { Title } from '@angular/platform-browser';
import { AngularRoutingProvider, bugsnagBootstrapper } from '@bugsnag/angular-performance';
import { NgxMatomoRouterModule } from '@ngx-matomo/router';
import * as i1$1 from '@ngx-matomo/tracker';
import { MatomoTracker, MatomoInitializerService, NgxMatomoTrackerModule, MatomoInitializationMode } from '@ngx-matomo/tracker';
import * as i2$1 from 'rxjs';
import { of, BehaviorSubject, combineLatest, ReplaySubject, Subject, Observable, interval, take, isObservable, from, switchMap } from 'rxjs';
import { filter, map, startWith, catchError, shareReplay, tap, share, distinctUntilChanged, takeWhile, mergeMap, concatMap, takeUntil, debounceTime } from 'rxjs/operators';
import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import * as _ from 'lodash';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { WebSocketSubject } from 'rxjs/webSocket';
import * as i4 from '@ngxs/store';
import swal from 'sweetalert2';
import * as i1$3 from 'ng-recaptcha';
import { RECAPTCHA_LANGUAGE, RECAPTCHA_V3_SITE_KEY, RecaptchaModule, RecaptchaV3Module } from 'ng-recaptcha';

/**
 * Basic animations
 */
class AnimationDefinitions {
}
AnimationDefinitions.SlideDown = trigger('slideDown', [
    state('void', style({ height: '0', opacity: '0' })),
    state('*', style({ height: '*', opacity: '1' })),
    transition('void => *', animate('250ms ease-in-out')),
    transition('* => void', animate('200ms ease-in-out')),
]);
AnimationDefinitions.ExpandCollapseHeight = trigger('expandCollapseHeight', [
    state('void', style({ height: '0', overflow: 'hidden' })),
    transition(':enter, :leave', [animate('250ms ease-in-out')]),
]);
AnimationDefinitions.ExpandCollapseWidth = trigger('expandCollapseWidth', [
    state('void', style({ width: '0' })),
    transition(':enter, :leave', [animate('250ms ease-in-out')]),
]);

class AppBasicsConstants {
}
/** Where to look for app-info.json relative to the app root   */
AppBasicsConstants.DEV_JSON_URL = 'assets/app-info.json';

class AppInfoService {
    constructor(httpClient, version, production) {
        this.httpClient = httpClient;
        this.version = version;
        this.production = production;
        this.DEV_JSON_URL = AppBasicsConstants.DEV_JSON_URL;
        this.appInfo$ = this.getJson$();
        /** Retruns Observable with current display version. */
        this.displayVersion$ = this.createDisplayVersion$();
        /** Retruns Observable with current branch name. */
        this.branchName$ = this.createBranchName$();
    }
    createBranchName$() {
        return this.appInfo$.pipe(filter(() => !this.production), map(response => {
            return response.branchName;
        }), startWith(null), catchError(() => of(null)));
    }
    createDisplayVersion$() {
        return this.appInfo$.pipe(map(response => {
            const displayVersion = response.displayVersion;
            return displayVersion && displayVersion?.length > 1 ? displayVersion : this.version;
        }), startWith(this.version), catchError(() => of(this.version)));
    }
    getJson$() {
        return this.httpClient.get(this.DEV_JSON_URL).pipe(map(response => response), shareReplay({ bufferSize: 1, refCount: true }));
    }
}
AppInfoService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppInfoService, deps: [{ token: i1.HttpClient }, { token: 'VERSION' }, { token: 'PRODUCTION' }], target: i0.ɵɵFactoryTarget.Injectable });
AppInfoService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppInfoService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppInfoService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['VERSION']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['PRODUCTION']
                }] }]; } });

/**
 * Component to display version and branch
 */
class AppInfoComponent {
    /** @ignore */
    constructor(devService) {
        this.devService = devService;
        /** What do you want to show */
        this.type = 'both';
        /** Separator between version and branch  */
        this.separator = ` ● `;
        /** Additional classes on span */
        this.classList = ``;
        /** @ignore */
        this.branchName$ = this.devService.branchName$;
        /** @ignore */
        this.displayVersion$ = this.devService.displayVersion$;
    }
}
AppInfoComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppInfoComponent, deps: [{ token: AppInfoService }], target: i0.ɵɵFactoryTarget.Component });
AppInfoComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: AppInfoComponent, selector: "mds-app-info", inputs: { type: "type", separator: "separator", classList: "classList" }, ngImport: i0, template: "<span *ngIf=\"type === 'version' || type === 'both'\" [ngClass]=\"classList\">{{ displayVersion$ | async }}</span>\n<ng-container *ngIf=\"branchName$ | async as bName\">\n    <span *ngIf=\"separator && type === 'both'\" [innerHTML]=\"separator\" [ngClass]=\"classList\"></span>\n    <span *ngIf=\"type === 'branch' || type === 'both'\" [ngClass]=\"classList\">{{ bName }}</span>\n</ng-container>\n", directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], pipes: { "async": i2.AsyncPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppInfoComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mds-app-info', styles: [], template: "<span *ngIf=\"type === 'version' || type === 'both'\" [ngClass]=\"classList\">{{ displayVersion$ | async }}</span>\n<ng-container *ngIf=\"branchName$ | async as bName\">\n    <span *ngIf=\"separator && type === 'both'\" [innerHTML]=\"separator\" [ngClass]=\"classList\"></span>\n    <span *ngIf=\"type === 'branch' || type === 'both'\" [ngClass]=\"classList\">{{ bName }}</span>\n</ng-container>\n" }]
        }], ctorParameters: function () { return [{ type: AppInfoService }]; }, propDecorators: { type: [{
                type: Input
            }], separator: [{
                type: Input
            }], classList: [{
                type: Input
            }] } });

/**
 * Pipe helper for [].includes()
 */
class ArrayContainsPipe {
    /**
     *
     * @param value Value that we are looking or
     * @param values Array of values that we are searching in
     */
    transform(value, values) {
        return values.includes(value);
    }
}
ArrayContainsPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ArrayContainsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
ArrayContainsPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ArrayContainsPipe, name: "arrayContains", pure: false });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ArrayContainsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'arrayContains',
                    pure: false,
                }]
        }] });

class AppBasicsService {
    constructor() {
        this.user$ = new BehaviorSubject(null);
        this.businessSubject$ = new BehaviorSubject(null);
        this.contractor$ = new BehaviorSubject(null);
    }
    getUser$() {
        return this.user$;
    }
    /**
     * Set current user in your app. This will be used by various services (eg: Bugsnag, Matomo)
     *
     * @param user Current user in your app
     */
    setUser(user) {
        this.user$.next(user);
    }
    getBusinessSubject$() {
        return this.businessSubject$;
    }
    /**
     * Set current business subject in your app. This will be used by various services (eg: Bugsnag, Matomo)
     *
     * @param businessSubject Current business subject in your app
     */
    setBusinessSubject(businessSubject) {
        this.businessSubject$.next(businessSubject);
    }
    getContractor$() {
        return this.contractor$;
    }
    /**
     * Set current contractor in your app. This will be used by various services (eg: Bugsnag, Matomo)
     *
     * @param contractor Current contractor in your app
     */
    setContractor(contractor) {
        this.contractor$.next(contractor);
    }
}
AppBasicsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppBasicsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AppBasicsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppBasicsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppBasicsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * @ignore
 */
function bugsnagFactory(bugSnagConfig, releaseStage, production = true, version = '0.0.0', appInfoService, appBasicsService, bugsnagService) {
    if (!bugSnagConfig?.apiKey) {
        if (!production) {
            console.warn('BugSnag config or key missing. You should add one!');
        }
        return () => void 0;
    }
    if (!production) {
        console.warn('BugSnag disabled. We are not in production');
        return () => void 0;
    }
    if (Bugsnag.isStarted()) {
        console.error('Bugsnag was already started. Probably somewhere in original APP. Most likely you should remove it!');
        Bugsnag.notify('Bugsnag was already started. Probably somewhere in original APP. Most likely you should remove it!');
        return () => void 0;
    }
    const data = {
        ver: version,
        user: null,
        contractor: null,
        bs: null,
    };
    const bugsnag = Bugsnag.start({
        appVersion: data.ver,
        releaseStage: releaseStage,
        enabledReleaseStages: bugSnagConfig.enabledReleaseStages ?? ['production'],
        autoTrackSessions: false,
        maxBreadcrumbs: 50,
        enabledBreadcrumbTypes: [],
        ...bugSnagConfig,
    });
    return () => {
        combineLatest([
            appInfoService.displayVersion$,
            appBasicsService.getUser$(),
            appBasicsService.getContractor$(),
            appBasicsService.getBusinessSubject$(),
        ]).subscribe(([ver, user, contractor, bs]) => {
            if (ver) {
                data.ver = ver;
            }
            data.user = user;
            data.contractor = contractor;
            data.bs = bs;
        });
        BugsnagPerformance.start({
            apiKey: bugSnagConfig.apiKey,
            releaseStage: releaseStage,
            enabledReleaseStages: bugSnagConfig.enabledReleaseStages ?? ['production'],
            appVersion: data.ver,
            routingProvider: new AngularRoutingProvider(),
        });
        bugsnagService.bugsnagNotify$.subscribe(msg => {
            if (msg.metaData) {
                for (const key in msg.metaData) {
                    bugsnag.addMetadata(key, msg.metaData[key]);
                }
            }
            bugsnag.notify(msg.error);
            if (msg.metaData) {
                for (const key in msg.metaData) {
                    bugsnag.clearMetadata(key);
                }
            }
        });
        bugsnag.addOnError(e => {
            e.app.version = data.ver;
            const bs = data.bs;
            if (bs) {
                e.addMetadata('business-subject', bs);
            }
            const contractor = data.contractor;
            if (contractor) {
                e.addMetadata('contractor', contractor);
            }
            const user = data.user;
            if (user) {
                e.setUser(user.id.toString());
            }
            return true;
        });
        return;
    };
}

/**
 * It will intercept all API requests thru HttpClient.
 * All requests will be added to Bugsnag as breadcrumbs.
 * 400 and >500 error will be sent to bugsnag as errors.
 */
class BugsnagInterceptorService {
    intercept(request, next) {
        return next.handle(request.clone()).pipe(tap({
            next: (response) => this.sendAsBreadcrumbToBugSnag(request, response),
            error: (response) => this.sendAsBreadcrumbToBugSnag(request, response),
        }));
    }
    sendAsBreadcrumbToBugSnag(request, response) {
        if (!(request instanceof HttpRequest)) {
            return;
        }
        if (!(response instanceof HttpResponse) && !(response instanceof HttpErrorResponse)) {
            return;
        }
        if (!Bugsnag.isStarted()) {
            return;
        }
        const response_message = _.get(response, 'message', undefined);
        const response_error = _.get(response, 'error', undefined);
        const traceId = response.headers.get('X-Trace-Id');
        Bugsnag.leaveBreadcrumb('HttpRequest', {
            request_url: request.url,
            request_method: request.method,
            request_responseType: request.responseType,
            response_url: response.url,
            response_status: response.status,
            response_statusText: response.statusText,
            response_type: response.type,
            response_ok: response.ok,
            response_message: response_message,
            response_error: response_error,
            response_trace_id: traceId,
        }, 'request');
        if (response.status === 400 || response.status >= 500) {
            let error;
            if (_.get(response, 'message', undefined)) {
                error = new Error(response_message);
            }
            else {
                error = new Error(`API Error: ${request.url}`);
            }
            Bugsnag.notify(error);
        }
    }
}
BugsnagInterceptorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: BugsnagInterceptorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
BugsnagInterceptorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: BugsnagInterceptorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: BugsnagInterceptorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class BugsnagService {
    constructor() {
        this.bugsnagNotify$ = new ReplaySubject(0);
    }
    /**
     * Send custom handeled error to Bugsnag.
     *
     * @param msg Error
     * @param metaData Additional metadata you wan't to add. Contractor and BS are added automatically
     */
    bugsnagNotify(msg, metaData) {
        this.bugsnagNotify$.next({
            error: msg,
            metaData: metaData,
        });
    }
}
BugsnagService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: BugsnagService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
BugsnagService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: BugsnagService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: BugsnagService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * @ignore
 */
function errorHandlerFactory(production = true) {
    if (!production) {
        return new ErrorHandler();
    }
    return new BugsnagErrorHandler();
}

/**
 * @ignore
 */
function matomoFactory(matomoConfig, releaseStage, production = true, version = '0.0.0', appInfoService, appBasicsService, tracker, init) {
    return () => {
        if (!matomoConfig || matomoConfig.disabled) {
            console.warn('Matomo disabled in config');
            return;
        }
        if (!production) {
            console.warn('Matomo disabled. We are not in production');
            return;
        }
        if (releaseStage !== 'production') {
            console.warn('Matomo disabled. Only production release stage allowed');
            return;
        }
        if (!matomoConfig.siteId) {
            console.warn('Matomo disabled. siteId is missing in config');
            return;
        }
        if (init['injected']) {
            console.warn('Matomo already injected. Probably somewhere in original APP. Most likely you should remove it!');
            return;
        }
        const dimensions = {
            version: null,
            majorVersion: null,
            businessSubjectId: null,
            contractorId: null,
            ...matomoConfig?.dimensions,
        };
        appBasicsService.getUser$().subscribe(user => {
            if (user) {
                tracker.setUserId(user.id.toString());
            }
        });
        if (dimensions.version || dimensions.majorVersion) {
            appInfoService.displayVersion$.subscribe(ver => {
                if ((ver || version) && dimensions.version) {
                    tracker.setCustomDimension(dimensions.version, ver || version);
                }
                if ((ver || version) && dimensions.majorVersion) {
                    const versionMajor = (ver || version).slice(0, 1);
                    tracker.setCustomDimension(dimensions.majorVersion, versionMajor);
                }
            });
        }
        if (dimensions.businessSubjectId) {
            appBasicsService.getBusinessSubject$().subscribe(bs => {
                if (bs && dimensions.businessSubjectId) {
                    tracker.setCustomDimension(dimensions.businessSubjectId, bs.id.toString());
                }
            });
        }
        if (dimensions.contractorId) {
            appBasicsService.getContractor$().subscribe(contractor => {
                if (contractor && dimensions.contractorId) {
                    tracker.setCustomDimension(dimensions.contractorId, contractor.id.toString());
                }
            });
        }
        init.initializeTracker({
            trackerUrl: matomoConfig.trackerUrl || 'https://data.medifit.si/',
            siteId: matomoConfig.siteId,
        });
        return;
    };
}

/**
 * Sets app title in <title> tag
 *
 * @param title Default APP title
 * @param releaseStage
 * @param production
 */
function pageTitleFactory(title, releaseStage, production = true) {
    return () => {
        if (production) {
            return;
        }
        const subdomain = (/:\/\/([^/]+)/.exec(location.origin)?.[1]?.split(/[:.-]/)[0] || '').toUpperCase();
        const titlePart = subdomain === 'LOCALHOST' ? '🛠 ' : `🧪 ${subdomain} - `;
        title.setTitle(`${titlePart} ${releaseStage.slice(0, 7).toUpperCase()} | ${title.getTitle()}`);
    };
}

class AppBasicsModule {
    /**
     * @param config Define basic app info and config
     * */
    static forRoot(config) {
        return {
            ngModule: AppBasicsModule,
            providers: [
                { provide: 'VERSION', useValue: config.version },
                { provide: 'PRODUCTION', useValue: config.production },
                { provide: 'RELEASE_STAGE', useValue: config.releaseStage },
                { provide: 'BUGSNAG_CONFIG', useValue: config.bugSnag },
                { provide: 'MATOMO_CONFIG', useValue: config.matomo },
                {
                    provide: APP_INITIALIZER,
                    useFactory: pageTitleFactory,
                    deps: [Title, 'RELEASE_STAGE', 'PRODUCTION'],
                    multi: true,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: bugsnagFactory,
                    deps: ['BUGSNAG_CONFIG', 'RELEASE_STAGE', 'PRODUCTION', 'VERSION', AppInfoService, AppBasicsService, BugsnagService],
                    multi: true,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: matomoFactory,
                    deps: [
                        'MATOMO_CONFIG',
                        'RELEASE_STAGE',
                        'PRODUCTION',
                        'VERSION',
                        AppInfoService,
                        AppBasicsService,
                        MatomoTracker,
                        MatomoInitializerService,
                    ],
                    multi: true,
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: BugsnagInterceptorService,
                    multi: true,
                },
                { provide: ErrorHandler, useFactory: errorHandlerFactory, deps: ['PRODUCTION'] },
                bugsnagBootstrapper,
            ],
        };
    }
}
AppBasicsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppBasicsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AppBasicsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppBasicsModule, declarations: [AppInfoComponent, ArrayContainsPipe], imports: [CommonModule,
        NgxMatomoRouterModule, i1$1.NgxMatomoTrackerModule], exports: [AppInfoComponent, ArrayContainsPipe] });
AppBasicsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppBasicsModule, imports: [[
            CommonModule,
            NgxMatomoRouterModule,
            NgxMatomoTrackerModule.forRoot({
                mode: MatomoInitializationMode.AUTO_DEFERRED,
            }),
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppBasicsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AppInfoComponent, ArrayContainsPipe],
                    exports: [AppInfoComponent, ArrayContainsPipe],
                    imports: [
                        CommonModule,
                        NgxMatomoRouterModule,
                        NgxMatomoTrackerModule.forRoot({
                            mode: MatomoInitializationMode.AUTO_DEFERRED,
                        }),
                    ],
                }]
        }] });

var ObjectState;
(function (ObjectState) {
    ObjectState[ObjectState["UNINIT"] = 0] = "UNINIT";
    ObjectState[ObjectState["LOADING"] = 1] = "LOADING";
    ObjectState[ObjectState["RELOADING"] = 2] = "RELOADING";
    ObjectState[ObjectState["VALID"] = 3] = "VALID";
    ObjectState[ObjectState["ERROR"] = 4] = "ERROR";
})(ObjectState || (ObjectState = {}));

/**
 * Helpers for HttpClient
 */
class HttpClientService {
    constructor(httpClient, loginInfo$) {
        this.httpClient = httpClient;
        this.loginInfo$ = loginInfo$;
        this.loginInfo$.pipe().subscribe((loginInfo) => {
            this.loginInfo = loginInfo;
        });
    }
    post(url, req, options) {
        const httpOptions = {};
        if (options) {
            Object.assign(httpOptions, options);
        }
        return this.httpClient.post(url, req, httpOptions);
    }
    put(url, req, options) {
        const httpOptions = {};
        if (options) {
            Object.assign(httpOptions, options);
        }
        return this.httpClient.put(url, req, httpOptions);
    }
    delete(url, options) {
        const httpOptions = {};
        if (options) {
            Object.assign(httpOptions, options);
        }
        return this.httpClient.delete(url, httpOptions);
    }
    get(url, options) {
        const httpOptions = {};
        if (options) {
            Object.assign(httpOptions, options);
        }
        return this.httpClient.get(url, httpOptions);
    }
    getAttachment(url) {
        const options = {
            responseType: 'blob',
            observe: 'response',
        };
        const req = new HttpRequest('GET', url, options);
        return this.httpClient.request(req);
    }
    uploadFile(url, file, comment) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('comment', comment);
        const params = new HttpParams();
        const options = {
            params: params,
            reportProgress: true,
            responseType: 'text',
            headers: new HttpHeaders({ authorization: 'Bearer ' + this.loginInfo.accessToken }),
        };
        const req = new HttpRequest('POST', url, formData, options);
        return this.httpClient.request(req);
    }
    getHttpClient() {
        return this.httpClient;
    }
}
HttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HttpClientService, deps: [{ token: i1.HttpClient }, { token: 'LOGIN_INFO' }], target: i0.ɵɵFactoryTarget.Injectable });
HttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HttpClientService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HttpClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2$1.Observable, decorators: [{
                    type: Inject,
                    args: ['LOGIN_INFO']
                }] }]; } });

var LocalAvalaibleEnum;
(function (LocalAvalaibleEnum) {
    LocalAvalaibleEnum["READY"] = "";
    LocalAvalaibleEnum["WAITING"] = "\u010Cakam na dostop do kamere...";
    LocalAvalaibleEnum["NotAllowedError"] = "Dostop do kamere ali mikrofona ni mogo\u010D! Priporo\u010Damo spremembo nastavitev va\u0161ega brskalnika in operacijskega sistema. \u010Ce \u017Eelite nadaljevati brez kamere ali mikrofona, kliknite na \"Za\u010Dnite ePosvet\"";
    LocalAvalaibleEnum["PERMISSION_DENIED"] = "Dostop do kamere ali mikrofona ni mogo\u010D! Priporo\u010Damo spremembo nastavitev va\u0161ega brskalnika in operacijskega sistema. \u010Ce \u017Eelite nadaljevati brez kamere ali mikrofona, kliknite na \u201CZa\u010Dnite ePosvet";
    LocalAvalaibleEnum["NotFoundError"] = "Ni ustrezne kamere ali mikrofona! Preverite ali sta kamera in mikrofon priklju\u010Dena in omogo\u010Dena.";
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    LocalAvalaibleEnum["DEVICES_NOT_FOUND"] = "Ni ustrezne kamere ali mikrofona! Preverite ali sta kamera in mikrofon priklju\u010Dena in omogo\u010Dena.";
    LocalAvalaibleEnum["MEDIA_OPTION_INVALID"] = "Kamera je \u017Ee v uporabi ali ni ustrezne kakovosti.";
    LocalAvalaibleEnum["NOT_SUPPORTED"] = "Va\u0161 brskalnik ne podpira uporabe kamere.";
})(LocalAvalaibleEnum || (LocalAvalaibleEnum = {}));

var WsCallStateType;
(function (WsCallStateType) {
    WsCallStateType[WsCallStateType["DEVICE_TEST"] = -1] = "DEVICE_TEST";
    WsCallStateType[WsCallStateType["NONE"] = 0] = "NONE";
    WsCallStateType[WsCallStateType["CHANNEL_JOINED"] = 2] = "CHANNEL_JOINED";
    WsCallStateType[WsCallStateType["CALL_STARTING"] = 3] = "CALL_STARTING";
    WsCallStateType[WsCallStateType["CALL_STARTED"] = 4] = "CALL_STARTED";
    WsCallStateType[WsCallStateType["CALL_ENDING"] = 5] = "CALL_ENDING";
    WsCallStateType[WsCallStateType["CALL_ENDED"] = 6] = "CALL_ENDED";
})(WsCallStateType || (WsCallStateType = {}));

var WsMessageType;
(function (WsMessageType) {
    WsMessageType["CHANNEL_INFO"] = "channelInfo";
    WsMessageType["USER_INFO"] = "userInfo";
    WsMessageType["CHAT_DATA"] = "CHAT_DATA";
})(WsMessageType || (WsMessageType = {}));

/**
 * Websocket Channel Info
 */
class WebSocketChannelInfo {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    deserialize(input) {
        this.doctorSessionLength = input.doctorSessionLength;
        this.doctorStartTime = input.doctorStartTime;
        this.isRecorded = input.isRecorded;
        this.isVideo = input.isVideo;
        this.patientSessionLength = input.patientSessionLength;
        this.patientStartTime = input.patientStartTime;
        if (input.startTime) {
            this.startTime = input.startTime;
        }
        else if (this.doctorStartTime && this.patientStartTime) {
            this.startTime = this.patientStartTime < this.doctorStartTime ? this.patientStartTime : this.doctorStartTime;
        }
        else if (this.doctorStartTime) {
            this.startTime = this.doctorStartTime;
        }
        else if (this.patientStartTime) {
            this.startTime = this.patientStartTime;
        }
        return this;
    }
}

/**
 * Websocket Channel
 */
class WebSocketChannel {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    deserialize(input) {
        this.activeWS = input.activeWS;
        this.activeWSSubscription = input.activeWSSubscription;
        this.channelData = input.channelData;
        this.channelData$ = input.channelData$;
        this.remoteClients = input.remoteClients;
        this.patientReady = input.remoteReady;
        this.channelInfo = input.channelInfo;
        this.channelInfo$ = input.channelInfo$;
        this.remoteClient = input.remoteClient;
        this.remoteClient$ = input.remoteClient$;
        this.chatData$ = input.chatData$;
        return this;
    }
}

/**
 * Websocket Channel User
 */
class WebSocketChannelUser {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    deserialize(input) {
        this.userName = input.userName;
        this.userId = input.userId;
        //this.displayName = input.displayName;
        this.userType = input.userType === 'Doctor' || input.userType === 'doctor' ? 'doctor' : 'patient';
        this.onTerm = input.onTerm ? input.onTerm : false;
        this.videoModalOpened = input.videoModalOpened ? input.videoModalOpened : false;
        this.cameraReady = input.cameraReady ? input.cameraReady : false;
        this.cameraError = input.cameraError ? input.cameraError : false;
        this.cameraMuted = input.cameraMuted ? input.cameraMuted : false;
        this.microphoneReady = input.microphoneReady ? input.microphoneReady : false;
        this.microphoneError = input.microphoneError ? input.microphoneError : false;
        this.microphoneMuted = input.microphoneMuted ? input.microphoneMuted : false;
        this.playoutMuted = input.playoutMuted ? input.playoutMuted : false;
        this.clientReady = input.clientReady ? input.clientReady : false;
        this.clientJoined = input.clientJoined ? input.clientJoined : false;
        this.streamPublished = input.streamPublished ? input.streamPublished : false;
        this.inCall = input.inCall ? input.inCall : false;
        this.uid = input.uid ? input.uid : null;
        this.timestamp = input.timestamp ? input.timestamp : new Date();
        this.peer = input.peer;
        return this;
    }
}

/**
 * Websocket Message
 */
class WsMessage {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    deserialize(input) {
        this.messageType = input.messageType;
        this.data = input.data;
        return this;
    }
}

/**
 * RxWebsocketSubject
 */
class RxWebsocketSubject extends Subject {
    constructor(url, 
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    multiplex = {
        subMsg: () => {
            return { a: 'b' };
        },
        unsubMsg: null,
        messageFilter: null,
    }, reconnectInterval = 3000, /// pause between connections
    reconnectAttempts = 100, /// number of connection attempts
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resultSelector) {
        super();
        this.url = url;
        this.multiplex = multiplex;
        this.reconnectInterval = reconnectInterval;
        this.reconnectAttempts = reconnectAttempts;
        this.resultSelector = resultSelector;
        this.closingConnection = false;
        /// by default, when a message is received from the server, we are trying to decode it as JSON
        /// we can override it in the constructor
        this.defaultResultSelector = (e) => {
            return JSON.parse(e.data);
        };
        /// connection status
        this.connectionStatus = new Observable(observer => {
            this.connectionObserver = observer;
        }).pipe(share(), distinctUntilChanged());
        //.share()
        //.distinctUntilChanged();
        if (!resultSelector) {
            this.resultSelector = this.defaultResultSelector;
        }
        /// config for WebSocketSubject
        /// except the url, here is closeObserver and openObserver to update connection status
        this.wsSubjectConfig = {
            url: url,
            closeObserver: {
                // @TODO: Ignored with eslint-interactive on 2023-10-27
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                next: (e) => {
                    this.socket = null;
                    this.connectionObserver?.next(false);
                },
            },
            openObserver: {
                // @TODO: Ignored with eslint-interactive on 2023-10-27
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                next: (e) => {
                    this.connectionObserver?.next(true);
                },
            },
        };
        /// we connect
        this.connect();
        /// we follow the connection status and run the reconnect while losing the connection
        this.connectionStatus.subscribe(isConnected => {
            if (!this.reconnectionObservable && typeof isConnected === 'boolean' && !isConnected) {
                this.reconnect();
            }
        });
    }
    close() {
        if (this.socket) {
            this.closingConnection = true;
            this.socketSubscription?.unsubscribe();
            //this.socket.complete();
            this.complete();
            this.socket = null;
        }
    }
    connect() {
        this.socket = new WebSocketSubject(this.wsSubjectConfig);
        if (this.multiplex) {
            //this.socket.;
        }
        this.socketSubscription = this.socket.multiplex(this.multiplex.subMsg, this.multiplex.unsubMsg, this.multiplex.messageFilter).subscribe(m => {
            this.next(m); /// when receiving a message, we just send it to our Subject
        }, (error) => {
            console.error(error);
            //if (!this.socket) {
            /// in case of an error with a loss of connection, we restore it
            this.reconnect();
            //}
        });
    }
    /// WebSocket Reconnect handling
    reconnect() {
        if (this.closingConnection) {
            return;
        }
        this.reconnectionObservable = interval(this.reconnectInterval).pipe(takeWhile((v, index) => {
            return index < this.reconnectAttempts && !this.socket;
        }));
        this.reconnectionObservable.subscribe({
            next: () => {
                this.connect();
            },
            complete: () => {
                /// if the reconnection attempts are failed, then we call complete of our Subject and status
                this.reconnectionObservable = null;
                if (!this.socket) {
                    this.complete();
                    this.connectionObserver?.complete();
                }
            },
        });
    }
    /// sending the message
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    send(data) {
        if (this.socket) {
            this.socket.next(data);
        }
    }
}

/**
 * Storage Service
 */
class StorageService {
    // @TODO: Ignored with eslint-interactive on 2024-02-13
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    constructor() { }
    getItem(key) {
        return localStorage.getItem(key);
    }
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setItem(key, content) {
        if (typeof content !== 'string') {
            content = JSON.stringify(content);
        }
        localStorage.setItem(key, content);
    }
    removeItem(key) {
        localStorage.removeItem(key);
    }
    getChannelId() {
        return this.get('channelId');
    }
    setChannelId(chid) {
        this.set('channelId', chid);
    }
    getAgoraToken() {
        return this.get('agoraToken');
    }
    setAgoraToken(chid) {
        this.set('agoraToken', chid);
    }
    //getUserId
    getUserId() {
        return this.get('userId');
    }
    setUserId(data) {
        this.set('userId', data);
    }
    //getChannelUserId
    getChannelUserId() {
        return this.get('userWSid');
    }
    setChannelUserId(data) {
        this.set('userWSid', data);
    }
    //getSocketSessionId
    getSocketSessionId() {
        return this.get('socketSessionId');
    }
    setSocketSessionId(data) {
        this.set('socketSessionId', data);
    }
    clearVendor() {
        sessionStorage.removeItem('vendor');
    }
    //userType
    getUserType() {
        return this.get('userType');
    }
    setUserType(data) {
        this.set('userType', data);
    }
    setAccessToken(token) {
        this.set('accessToken', token);
    }
    removeAccessToken() {
        sessionStorage.removeItem('accessToken');
    }
    //userType
    getGUID() {
        return this.get('guid-token');
    }
    setGUID(data) {
        this.set('guid-token', data);
    }
    clearFullStorage() {
        localStorage.clear();
        sessionStorage.clear();
    }
    clearBasicStorage() {
        this.clearVendor();
    }
    //////////////////////////////
    //////////////////////////////
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(name) {
        // @TODO: Ignored with eslint-interactive on 2023-10-27
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = sessionStorage.getItem(name);
        try {
            return JSON.parse(data);
        }
        catch (e) {
            return data;
        }
    }
    set(name, data) {
        sessionStorage.setItem(name, JSON.stringify(data));
    }
}
StorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: StorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
StorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: StorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: StorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Telemedicine Service
 */
class TelemedicineService {
    constructor(loginInfo$, environment, HttpClientService) {
        this.loginInfo$ = loginInfo$;
        this.environment = environment;
        this.HttpClientService = HttpClientService;
        this.loginInfo$.pipe().subscribe((loginInfo) => {
            this.loginInfo = loginInfo;
        });
    }
    getWebSocketTelemedUrl() {
        return (this.environment.eposvet.webSocketTelemed.protocol +
            this.environment.eposvet.webSocketTelemed.rootUrl +
            this.environment.eposvet.webSocketTelemed.port +
            this.environment.eposvet.webSocketTelemed.url);
    }
    getTelemedUrl() {
        //http://localhost:9090/booking/patient/api
        return (this.environment.eposvet.telemed.protocol +
            this.environment.eposvet.telemed.rootUrl +
            this.environment.eposvet.telemed.port +
            this.environment.eposvet.telemed.url);
    }
    getEPosvetUrl() {
        //http://localhost:9090/booking/patient/api
        return (this.environment.eposvet.ePosvet.protocol +
            this.environment.eposvet.ePosvet.rootUrl +
            this.environment.eposvet.ePosvet.port +
            this.environment.eposvet.ePosvet.url);
    }
    getTokenData(isDoctor, channel, vendor, contractorId) {
        let tokenUrl = '';
        if (contractorId) {
            tokenUrl = `${this.getTelemedUrl()}/contractors/${contractorId}/prereservations/${channel}`;
        }
        if (vendor) {
            tokenUrl = `${this.getEPosvetUrl()}/vendors/${vendor}/prereservations/${channel}`;
        }
        const accessToken = this.loginInfo.accessToken;
        return this.HttpClientService.post(tokenUrl, {
            appId: this.environment.eposvet.agora.appId,
            isDoctor: isDoctor,
        }, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }).pipe(catchError(error => {
            return of(error);
        }));
    }
}
TelemedicineService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TelemedicineService, deps: [{ token: 'LOGIN_INFO' }, { token: 'ENVIRONMENT' }, { token: HttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
TelemedicineService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TelemedicineService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TelemedicineService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i2$1.Observable, decorators: [{
                    type: Inject,
                    args: ['LOGIN_INFO']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['ENVIRONMENT']
                }] }, { type: HttpClientService }]; } });

/**
 * Case Service
 */
class CaseService {
    constructor() {
        this.observer = new ReplaySubject(1);
    }
    emitData(data) {
        this.observer.next(data);
    }
}
CaseService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: CaseService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CaseService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: CaseService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: CaseService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

// TODO cleanup ts-ignore after eslint is fixed
/**
 * Websocket client service
 */
class WebsocketClientService {
    constructor(storage, telemedRest, caseService, store, loginInfo$) {
        this.storage = storage;
        this.telemedRest = telemedRest;
        this.caseService = caseService;
        this.store = store;
        this.loginInfo$ = loginInfo$;
        this.channels = [];
        this.userType = 'patient';
        // @TODO: Ignored with eslint-interactive on 2023-10-27
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.connectionStatus$ = new BehaviorSubject(false);
        this.outsideCallInProgress$ = new BehaviorSubject(false);
        this.callState$ = new BehaviorSubject(WsCallStateType.NONE);
        this.callState = WsCallStateType.NONE;
        this.$callTimer = new BehaviorSubject(0);
        this.callTotalTime = null;
        // @TODO: Ignored with eslint-interactive on 2023-10-27
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.$callTotalTime = sessionStorage.getItem('callTotalTime') ? Number(sessionStorage.getItem('callTotalTime')) : 0;
        this.timer = null;
        // @TODO: Ignored with eslint-interactive on 2023-10-27
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.initWebSocketResolver$ = new Subject();
        this.loginInfo$.pipe().subscribe((loginInfo) => {
            this.loginInfo = loginInfo;
        });
    }
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initWebSocket(channelId) {
        if (channelId) {
            // this.telemedRest.getReservationInfo(channelId).subscribe((response) => {
            // this.storage.setAccessToken(response?.accessToken);
            // this.storage.setContractorId(response?.contractorId);
            // this.storage.setVendor(response?.vendor);
            const userData = this.parseUserData({
                doctorId: 'TEST DOKTOR',
                patientName: 'TEST PATIENT',
                patientSurname: 'TEST SURNAME',
                patientId: this.loginInfo.maskedPatientId,
            });
            //this.storage.setUserId(response.uid);
            //const userWSid = getSocketUserId(userId);
            //this.storage.setAgoraToken(response.token);
            //this.storage.setChannelUserId(userWSid);
            this.initSocketChannel(channelId, userData);
            this.startSocketConnection(channelId);
            //this.isLoaded = true;
            // });
        }
        return this.initWebSocketResolver$;
    }
    initSocketChannel(channelId, userData) {
        if (!this.channels[channelId]) {
            // debugger;
            this.channels[channelId] = new WebSocketChannel().deserialize({
                activeWS: null,
                activeWSSubscription: null,
                channelInfo: new WebSocketChannelInfo().deserialize({}),
                channelData: userData,
                channelData$: new BehaviorSubject(userData),
                remoteClients: [],
                remoteClient: null,
                remoteClient$: new BehaviorSubject(null),
                //remoteReady: new BehaviorSubject({status: false, description: null}),
                channelInfo$: new BehaviorSubject(null),
                chatData$: new BehaviorSubject(null),
            });
        }
    }
    getChatData(channelId) {
        if (!channelId) {
            return of();
        }
        return this.channels[channelId].chatData$;
    }
    startSocketConnection(channelId) {
        if (!channelId) {
            return;
        }
        if (!this.channels[channelId].activeWS) {
            const socketUserId = this.channels[channelId].channelData.userId;
            const userName = this.channels[channelId].channelData.userName;
            const accessToken = this.loginInfo.accessToken;
            // debugger;
            this.channels[channelId].activeWS = new RxWebsocketSubject(this.telemedRest.getWebSocketTelemedUrl() + `${channelId}/${socketUserId}/${userName}?auth_token=${accessToken}`, {
                subMsg: () => this.createSocketMessage(channelId, new WebSocketChannelUser().deserialize({
                    onTerm: true,
                    userName: 'test',
                    userId: 'test2', // TODO REMOVE
                })),
                unsubMsg: () => {
                    const channelData = new WebSocketChannelUser().deserialize({
                        userName: this.channels[channelId].channelData.userName,
                        userId: this.channels[channelId].channelData.userId,
                        displayName: this.channels[channelId].channelData.displayName,
                        userType: this.channels[channelId].channelData.userType,
                    });
                    this.setChannelData(channelId, channelData);
                    this.sendSocketAddTimeMessage(channelId);
                    return channelData;
                },
                // @TODO: Ignored with eslint-interactive on 2023-10-27
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                messageFilter: (message) => {
                    if (_.isEmpty(message)) {
                        return false;
                    }
                    if (message.eventType == 'chat') {
                        return true;
                    }
                    if (message.eventType) {
                        return false;
                    }
                    if (!message.doctorStartTime && !message.patientStartTime && !message.userId) {
                        return false;
                    }
                    return true;
                },
            });
            this.channels[channelId].activeWSSubscription = this.channels[channelId].activeWS
                .pipe(tap(() => {
                this.initWebSocketResolver$.next(true);
            }), 
            // @TODO: Ignored with eslint-interactive on 2023-10-27
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((message) => {
                if (message.doctorStartTime || message.patientStartTime) {
                    //TODO WsMessage objekt zamenjaj z AS
                    return {
                        messageType: 'channelInfo',
                        data: message,
                    };
                }
                else if (message.userId) {
                    return {
                        messageType: 'userInfo',
                        data: message,
                    };
                }
                else if (message.eventType == 'chat') {
                    return new WsMessage().deserialize({
                        messageType: WsMessageType.CHAT_DATA,
                        data: _.get(message, 'chat', []),
                    });
                }
                else {
                    console.error('Unknown message from socket');
                    return null;
                }
            }))
                .subscribe((message) => {
                if (message.messageType === 'channelInfo') {
                    this.channels[channelId].channelInfo = new WebSocketChannelInfo().deserialize(message.data);
                    this.channels[channelId].channelInfo$.next(this.channels[channelId].channelInfo);
                }
                if (message.messageType === 'userInfo') {
                    if (message.data.userId === this.channels[channelId].channelData.userId) {
                        return;
                    }
                    if (message.data.onTerm) {
                        this.addRemote(channelId, message.data);
                    }
                    else {
                        this.removeRemote(channelId, message.data);
                    }
                }
                if (message.messageType === WsMessageType.CHAT_DATA) {
                    this.channels[channelId].chatData$.next(message.data);
                }
                this.channels[channelId].activeWS.connectionStatus.subscribe(status => {
                    // TODO FIGURE STATUSES
                    // debugger;
                    this.connectionStatus$.next(status);
                });
            });
            // @TODO Clear intervals na leave
            setInterval(() => {
                this.sendSocketAddTimeMessage(channelId);
            }, 60000);
        }
    }
    //TODO CHECK IF NEEDED
    closeSocket(channelId) {
        if (this.channels[channelId]) {
            if (this.channels[channelId].activeWSSubscription) {
                this.channels[channelId].activeWSSubscription.unsubscribe();
            }
            if (this.channels[channelId].activeWS) {
                this.channels[channelId].activeWS.close();
            }
            this.channels[channelId] = undefined;
            if (this.timer) {
                this.timer.unsubscribe();
            }
        }
    }
    sendSocketStartMessage(channelId) {
        if (this.channels[channelId]) {
            this.channels[channelId].activeWS.send({
                eventType: 'start',
                source: this.getLocalUserType(channelId) === 'doctor' ? 'DOCTOR' : 'PATIENT',
                videoSession: true,
                recordedSession: false,
            });
            if (!this.channels[channelId].channelInfo || !this.channels[channelId].channelInfo.doctorStartTime) {
                this.channels[channelId].channelInfo = new WebSocketChannelInfo().deserialize({
                    doctorStartTime: new Date().getTime(),
                });
                this.channels[channelId].channelInfo$.next(this.channels[channelId].channelInfo);
            }
            setTimeout(() => {
                this.sendSocketAddTimeMessage(channelId);
            }, 5000);
        }
    }
    sendSocketAddTimeMessage(channelId) {
        if (this.callState === WsCallStateType.CALL_STARTED && channelId) {
            if (this.channels[channelId]) {
                this.channels[channelId].activeWS.send({
                    eventType: 'ADDTIME',
                    source: this.getLocalUserType(channelId) === 'doctor' ? 'DOCTOR' : 'PATIENT',
                });
            }
        }
    }
    createSocketMessage(channelId, data) {
        if (!this.channels[channelId].channelData) {
            this.channels[channelId].channelData = new WebSocketChannelUser().deserialize(data);
            //this.channelData[channelId].userName = user.username;
        }
        else {
            this.channels[channelId].channelData = {
                ...this.channels[channelId].channelData,
                ...{ timestamp: new Date() },
                ...data,
            };
        }
        this.setChannelData(channelId, this.channels[channelId].channelData);
        return this.channels[channelId].channelData;
    }
    sendSocketMessage(channelId, data) {
        if (this.channels[channelId]) {
            const channel = this.channels[channelId].activeWS;
            const message = this.createSocketMessage(channelId, data);
            if (channel) {
                channel.send(message);
            }
        }
        else {
            console.error(`Error sending message to socket ${channelId}`);
        }
    }
    sendChatSocketMessage(channelId, message, documents) {
        // @TODO: Ignored with eslint-interactive on 2023-10-27
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = [
            {
                eventType: 'chat',
                source: this.getLocalUserType(channelId).toUpperCase() === 'DOCTOR' ? 'DOCTOR' : 'PATIENT',
                chat: message,
                username: this.getLocalUserId(channelId),
                userid: this.getLocalUserName(channelId),
                eventTime: new Date(),
            },
        ];
        // if (documents) {
        //     data.document = document;
        // }
        documents?.forEach((document, index) => {
            if (index == 0) {
                data[0].document = document;
            }
            else {
                data.push({
                    eventType: 'chat',
                    source: this.getLocalUserType(channelId).toUpperCase() === 'DOCTOR' ? 'DOCTOR' : 'PATIENT',
                    chat: null,
                    username: this.getLocalUserId(channelId),
                    userid: this.getLocalUserName(channelId),
                    eventTime: new Date(),
                    document: document,
                });
            }
        });
        if (this.channels[channelId]) {
            const channel = this.channels[channelId].activeWS;
            if (channel) {
                channel.send(data);
                this.channels[channelId].chatData$.next(data);
            }
        }
        else {
            console.error(`Error sending message to socket (generic) ${channelId}`);
        }
    }
    getCallState() {
        return this.callState$;
    }
    setCallState(state) {
        if (this.callState !== state) {
            this.callState = state;
            this.callState$.next(state);
            // if (this.callState !== WsCallStateType.CALL_STARTED) {
            //     this.toggleModalSize('modal');
            // }
        }
    }
    setChannelData(channelId, channelData) {
        this.channels[channelId].channelData = channelData;
        this.channels[channelId].channelData$.next(channelData);
    }
    getChannelData(channelId) {
        return new Observable((observer) => {
            const int$ = setInterval(() => {
                if (this.channels[channelId] && this.channels[channelId].channelData$) {
                    clearInterval(int$);
                    this.channels[channelId].channelData$.subscribe((res) => {
                        observer.next(res);
                    });
                }
            }, 100);
        });
    }
    getLocalUserType(channelId) {
        return this.channels[channelId]?.channelData?.userType;
    }
    getLocalUserId(channelId) {
        return this.channels[channelId]?.channelData?.userId;
    }
    getLocalUserName(channelId) {
        return this.channels[channelId]?.channelData?.userName;
    }
    getRemote(channelId) {
        if (!this.channels[channelId]) {
            return null;
        }
        const lookingFor = this.channels[channelId]?.channelData?.userType === 'doctor' ? 'patient' : 'doctor';
        let remoteCallIndex = this.channels[channelId].remoteClients.findIndex(remoteClient => {
            //return remoteClient.userType === 'patient' && uid ? uid === remoteClient.uid : true;
            return remoteClient.userType === lookingFor && remoteClient.onTerm && remoteClient.clientReady && remoteClient.streamPublished;
        });
        if (remoteCallIndex === -1) {
            remoteCallIndex = this.channels[channelId].remoteClients.findIndex(remoteClient => {
                //return remoteClient.userType === 'patient' && uid ? uid === remoteClient.uid : true;
                return remoteClient.userType === lookingFor && remoteClient.onTerm && remoteClient.clientReady;
            });
        }
        if (remoteCallIndex === -1) {
            remoteCallIndex = this.channels[channelId].remoteClients.findIndex(remoteClient => {
                //return remoteClient.userType === 'patient' && uid ? uid === remoteClient.uid : true;
                return remoteClient.userType === lookingFor && remoteClient.onTerm;
            });
        }
        if (remoteCallIndex === -1) {
            return null;
        }
        return this.channels[channelId].remoteClients[remoteCallIndex];
    }
    addRemote(channelId, data) {
        const existingClient = this.channels[channelId].remoteClients.findIndex((remoteClient) => {
            return remoteClient.userId === data.userId;
        });
        if (existingClient === -1) {
            this.channels[channelId].remoteClients.push(data);
        }
        else {
            this.channels[channelId].remoteClients[existingClient] = {
                ...this.channels[channelId].remoteClients[existingClient],
                ...data,
            };
        }
        this.checkRemotes(channelId);
    }
    removeRemote(channelId, data) {
        const existingClient = this.channels[channelId].remoteClients.findIndex(remoteClient => {
            return remoteClient.userId === data.userId || remoteClient.uid === data.uid;
        });
        if (existingClient !== -1) {
            this.channels[channelId].remoteClients.splice(existingClient, 1);
        }
        this.checkRemotes(channelId);
    }
    checkRemotes(channelId) {
        if (this.channels[channelId]) {
            const existingCall = this.channels[channelId].remoteClients.find(remoteClient => {
                //return remoteClient.userType === 'patient' && uid ? uid === remoteClient.uid : true;
                return (remoteClient.userType === this.channels[channelId]?.channelData?.userType && (remoteClient.inCall || remoteClient.streamPublished));
            });
            if (existingCall) {
                this.outsideCallInProgress$.next(true);
            }
            else {
                this.outsideCallInProgress$.next(false);
            }
        }
        else {
            this.outsideCallInProgress$.next(false);
        }
        const patientClient = this.getRemote(channelId);
        if (patientClient) {
            this.channels[channelId].remoteClient = patientClient;
            this.channels[channelId].remoteClient$.next(patientClient);
        }
        return patientClient;
    }
    getRemoteClient(channelId) {
        return new Observable((observer) => {
            const int$ = setInterval(() => {
                if (this.channels[channelId] && this.channels[channelId].remoteClient$) {
                    clearInterval(int$);
                    this.channels[channelId].remoteClient$.subscribe(res => {
                        observer.next(res);
                    });
                }
            }, 100);
        });
    }
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parseUserData(reservationInfo) {
        let userType = this.storage.getUserType();
        // debugger;
        let displayName = 'Uporabnik';
        let userName = 'Uporabnik';
        if (userType === 'Doctor') {
            displayName = 'Zdravnik';
            userName = reservationInfo?.doctorId;
            userType = 'doctor';
        }
        else {
            displayName = `${reservationInfo.patientName} ${reservationInfo.patientSurname}`;
            userName = reservationInfo?.patientId;
            userType = 'patient';
        }
        // debugger;
        return new WebSocketChannelUser().deserialize({
            userName: userName,
            userId: this.getSocketUserId(userName),
            displayName: displayName,
            userType: userType,
        });
    }
    endCall(channelId) {
        if (this.callState === WsCallStateType.CALL_STARTED) {
            this.setCallState(WsCallStateType.CALL_ENDING);
        }
        else {
            this.setCallState(WsCallStateType.DEVICE_TEST);
        }
        this.sendSocketMessage(channelId, new WebSocketChannelUser().deserialize({
            clientReady: false,
        }));
    }
    getOutsideCallInProgress() {
        return this.outsideCallInProgress$;
    }
    getConnectionStatus(channelId) {
        if (!channelId) {
            console.error('ni channel idja');
            this.connectionStatus$.next(false);
        }
        if (!(this.channels[channelId] && this.channels[channelId].activeWS)) {
            this.connectionStatus$.next(false);
        }
        return this.connectionStatus$;
    }
    getSessionId() {
        let sessionId = this.storage.getSocketSessionId();
        if (!sessionId) {
            sessionId = Math.random().toString();
            this.storage.setSocketSessionId(sessionId);
        }
        return sessionId;
    }
    getSocketUserId(userId) {
        const sessionId = this.getSessionId();
        return userId?.toString() + sessionId;
    }
    callTimer(e) {
        if (e) {
            this.$callTimer = interval(1000);
            this.timer = this.$callTimer.subscribe(n => {
                console.log(n);
                this.$callTotalTime = this.$callTotalTime + 1000;
                sessionStorage.setItem('callTotalTime', String(this.$callTotalTime));
                this.caseService.emitData(this.$callTotalTime);
            });
        }
        else {
            if (this.timer) {
                this.timer.unsubscribe();
            }
        }
    }
}
WebsocketClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: WebsocketClientService, deps: [{ token: StorageService }, { token: TelemedicineService }, { token: CaseService }, { token: i4.Store }, { token: 'LOGIN_INFO' }], target: i0.ɵɵFactoryTarget.Injectable });
WebsocketClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: WebsocketClientService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: WebsocketClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: StorageService }, { type: TelemedicineService }, { type: CaseService }, { type: i4.Store }, { type: i2$1.Observable, decorators: [{
                    type: Inject,
                    args: ['LOGIN_INFO']
                }] }]; } });

// TODO cleanup ts-ignore after eslint is fixed
class AgoraService {
    constructor(agoraConfig, loginInfo$, wsClient, telemedRest) {
        this.agoraConfig = agoraConfig;
        this.loginInfo$ = loginInfo$;
        this.wsClient = wsClient;
        this.telemedRest = telemedRest;
        this.telemedInitialised = false;
        this.appId = this.agoraConfig.appId;
        this.connected = false;
        this.outsideCallInProgress = false;
        this.setVolumeTo = 100;
        this.audioVolume = 100;
        this.audioVolume$ = new BehaviorSubject(100);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.channelId = null;
        //private remoteStreams: Stream[] = [];
        this.localAvalaible = {
            initial: null,
            final: LocalAvalaibleEnum.WAITING,
        };
        this.localAvalaible$ = new BehaviorSubject(this.localAvalaible);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.videoDevices$ = new BehaviorSubject([]);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.audioDevices$ = new BehaviorSubject([]);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.playoutDevices$ = new BehaviorSubject([]);
        this.activeDevices = {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            camera: null,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            audio: null,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            playout: null,
        };
        this.activeDevices$ = new BehaviorSubject(this.activeDevices);
        this.localAudioEnabled = false;
        this.localVideoEnabled = false;
        this.lastFallBackCombination = 0;
        this.fallBackCombinations = [
            {
                video: true,
                audio: true,
            },
            {
                video: true,
                audio: false,
            },
            {
                video: false,
                audio: true,
            },
        ];
        this.streamProfiles = {
            '720p': {
                agoraProfile: '720p_3',
                audio: true,
                video: true,
            },
            '480p': {
                agoraProfile: '480p_1',
                audio: true,
                video: true,
            },
        };
        this.loginInfo$.pipe().subscribe((loginInfo) => {
            this.loginInfo = loginInfo;
        });
    }
    // private proxyEnableContracotrs = [];
    async initAgora(channelId) {
        const response = new ReplaySubject(1);
        if (this.telemedInitialised) {
            response.next(true);
            response.complete();
            return response;
        }
        AgoraRTC.setLogLevel(this.agoraConfig.logLevel);
        if (channelId) {
            this.channelId = channelId;
        }
        if (!this.client) {
            const agoraConf = {
                mode: 'rtc',
                codec: 'vp8',
            };
            // if (
            //     this.proxyEnableContracotrs.findIndex((contractorId) => {
            //         return contractorId === String(this.loginInfo?.contractorId);
            //     }) !== -1
            // ) {
            //     agoraConf.proxyServer = environment.agora.proxyServer;
            //     agoraConf.turnServer = environment.agora.turnServer;
            // }
            this.client = AgoraRTC.createClient(agoraConf);
            this.assignClientHandlers();
        }
        else {
            response.next(true);
            response.complete();
        }
        this.telemedInitialised = true;
        return response;
    }
    initLocalMedia(uid, profileId, channelId) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.initAgora(channelId).then(async () => {
            const combination = this.fallBackCombinations[this.lastFallBackCombination];
            this.localAudio = await AgoraRTC.createMicrophoneAudioTrack();
            try {
                this.localVideo = await AgoraRTC.createCameraVideoTrack({
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    encoderConfig: this.getProfile(profileId).agoraProfile,
                });
                this.setLocalAvalaible(LocalAvalaibleEnum.READY);
                console.log('getUserMedia successfully');
                this.connected = false;
                this.toggleAudio(combination.audio);
                this.toggleVideo(combination.video);
                console.log(this.localStream);
                this.localVideo.play('agora-local-video');
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                this.wsClient.sendSocketMessage(channelId, {
                    cameraReady: combination.video,
                    microphoneReady: combination.audio,
                    cameraError: !combination.video,
                    microphoneError: !combination.audio,
                });
            }
            catch (e) {
                console.error('getUserMedia failed', e);
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                this.setLocalAvalaible(LocalAvalaibleEnum[e.msg]);
                this.lastFallBackCombination++;
                if (this.lastFallBackCombination < this.fallBackCombinations.length) {
                    this.localStream.close();
                    this.initLocalMedia(uid);
                    return;
                }
                this.toggleAudio(false);
                this.toggleVideo(false);
                this.wsClient.sendSocketMessage(this.channelId, {
                    cameraReady: false,
                    cameraError: true,
                    microphoneReady: false,
                    microphoneError: true,
                });
            }
            this.assignLocalStreamHandlers();
        });
    }
    assignLocalStreamHandlers() {
        this.loadCameras();
        this.loadRecordingDevices();
        this.loadPlayoutDevices();
    }
    assignClientHandlers() {
        this.client.on('connection-state-change', evt => {
            if (evt === 'CONNECTED') {
                this.wsClient.sendSocketMessage(this.channelId, {
                    streamPublished: true,
                });
                console.log('Publish local stream successfully');
            }
        });
        this.client.on('exception', error => {
            console.error('Got error msg:', error.msg);
            if (error.msg === 'DYNAMIC_KEY_TIMEOUT') {
                try {
                    this.client.renewToken('');
                    console.log('Renewed the channel key successfully.');
                }
                catch (e) {
                    console.error('Renew channel key failed: ', e);
                }
            }
            // if (error.reason === 'SOCKET_DISCONNECTED') {
            //     this.lostConnection = true;
            // }
        });
        // this.client.on('user-published', async (remoteUser, mediaType) => {
        //     await this.client.subscribe(remoteUser, mediaType);
        //     if (mediaType === 'video') {
        //         console.log('subscribe video success');
        //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //         // @ts-ignore
        //         remoteUser.videoTrack.play('agora-remote-video');
        //     }
        //     if (mediaType === 'audio') {
        //         console.log('subscribe audio success');
        //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //         // @ts-ignore
        //         remoteUser.audioTrack.play();
        //     }
        // });
        this.client.on('user-published', async (user, mediaType) => {
            await this.client.subscribe(user, mediaType);
            if (mediaType === 'video') {
                console.log('subscribe video success');
                user.videoTrack.play('agora-remote-video');
            }
            if (mediaType === 'audio') {
                console.log('subscribe audio success');
                user.audioTrack.play();
            }
        });
        this.client.enableAudioVolumeIndicator();
        this.client.on('volume-indicator', function (result) {
            result.forEach(function (volume, index) {
                console.log(`${index} UID ${volume.uid} Level ${volume.level}`);
            });
        });
        this.client.on('network-quality', (stats) => {
            console.log('downlinkNetworkQuality', stats.downlinkNetworkQuality);
            console.log('uplinkNetworkQuality', stats.uplinkNetworkQuality);
        });
        // this.client.on(ClientEvent.RemoteStreamAdded, (evt) => {
        //     const newStream = (this.remoteStream = evt.stream as Stream);
        //
        //     if (!this.outsideCallInProgress) {
        //         this.client.subscribe(newStream, { audio: true, video: true }, (err) => {
        //             console.error('Subscribe stream failed', err);
        //         });
        //         this.setAudioVolume(this.setVolumeTo);
        //     } else {
        //         this.connected = false;
        //         this.client.leave();
        //     }
        // });
        //
        // this.client.on(ClientEvent.RemoteStreamRemoved, (evt) => {
        //     const oldStream = (this.remoteStream = evt.stream as Stream);
        //
        //
        //     if (oldStream) {
        //         oldStream.stop();
        //         oldStream.close();
        //         console.log(`Remote stream is removed ${oldStream.getId()}`);
        //     }
        // });
        this.client.on('user-joined', evt => {
            const stream = (this.remoteStream = evt.videoTrack);
            this.remoteAudioStream = evt.audioTrack;
            this.wsClient.setCallState(WsCallStateType.CALL_STARTED);
            // @TODO: Ignored with eslint-interactive on 2023-10-27
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            this.setAudioVolume(this.setVolumeTo).then(r => { });
            this.wsClient.sendSocketMessage(this.channelId, {
                inCall: true,
            });
            this.wsClient.sendSocketStartMessage(this.channelId);
            setTimeout(() => {
                console.log('stream.play(agora-remote-video)');
                stream.play('agora-remote-video');
                // stream.play('agora-remote-video');
            }, 50);
            this.wsClient.callTimer(true);
        });
        // this.client.on('user-joined', evt => {
        //
        //   console.log('User joined', evt);
        //   const stream = (this.remoteStream = evt.videoTrack as IRemoteVideoTrack);
        //   this.remoteAudioStream = evt.audioTrack as IRemoteAudioTrack;
        //   this.setCallState(CallStateEnum.CALL_STARTED);
        //   // @TODO: Ignored with eslint-interactive on 2023-10-27
        //   // eslint-disable-next-line @typescript-eslint/no-unused-vars
        //   // eslint-disable-next-line @typescript-eslint/no-empty-function
        //   this.setAudioVolume(this.setVolumeTo).then(r => {});
        //
        //   this.sendSocketMessage(this.channelId, {
        //     inCall: true,
        //   });
        //   this.sendSocketStartMessage(this.channelId);
        //
        //   setTimeout(() => {
        //     stream.play('agora-remote-video');
        //   }, 50);
        //
        //   this.callTimer(true);
        // });
        this.client.on('user-left', (evt) => {
            const oldStream = (this.remoteStream = evt.videoTrack);
            if (oldStream) {
                oldStream.stop();
                // oldStream.close();
                console.log(`${evt.uid} left from this channel`);
            }
        });
        this.client.on('user-published', evt => {
            this.remoteUser = evt;
            console.log(`${evt.uid} online on agora`);
        });
    }
    join(remoteClient) {
        if (this.connected) {
            return;
        }
        this.telemedRest
            .getTokenData(this.wsClient.getLocalUserType(this.channelId) === 'doctor', this.channelId, undefined, this.loginInfo.contractorId + '')
            .subscribe(async (response) => {
            try {
                await this.client.join(this.appId, this.channelId, response.token, response.uid);
                this.wsClient.sendSocketMessage(this.channelId, {
                    clientJoined: true,
                    uid: response.uid,
                });
                this.connected = true;
                setTimeout(() => {
                    this.publish();
                }, 10);
                // @TODO Should be turned around
                if (remoteClient.cameraError && remoteClient.microphoneError) {
                    if (!this.outsideCallInProgress) {
                        this.setAudioVolume(this.setVolumeTo);
                        this.wsClient.setCallState(WsCallStateType.CALL_STARTED);
                        this.wsClient.sendSocketMessage(this.channelId, {
                            inCall: true,
                        });
                        this.wsClient.sendSocketStartMessage(this.channelId);
                    }
                    else {
                        this.wsClient.sendSocketMessage(this.channelId, {
                            streamPublished: false,
                        });
                        this.connected = false;
                        this.client.leave();
                    }
                }
            }
            catch (e) {
                this.wsClient.sendSocketMessage(this.channelId, {
                    clientJoined: false,
                    inCall: false,
                    uid: response.uid,
                });
            }
        });
    }
    leave() {
        console.log('leave', this.connected);
        if (this.connected) {
            this.unpublish();
            this.unpublish();
            // @TODO: Ignored with eslint-interactive on 2023-10-27
            // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-empty-function
            this.client.leave().then(r => { });
            this.localAudio.close();
            this.localVideo.close();
            this.client.on('connection-state-change', (curState, prevState) => {
                console.log('current', curState, 'prev', prevState);
            });
            this.client.on('user-left', (evt, reason) => {
                console.log(evt);
                console.log(reason);
                this.connected = false;
                this.wsClient
                    .getCallState()
                    .pipe(take(1))
                    .subscribe(callState => {
                    if (callState === WsCallStateType.CALL_STARTING) {
                        this.wsClient.setCallState(WsCallStateType.DEVICE_TEST);
                    }
                    if (callState === WsCallStateType.CALL_STARTED) {
                        this.wsClient.setCallState(WsCallStateType.CALL_ENDING);
                    }
                    this.wsClient.sendSocketMessage(this.channelId, {
                        clientJoined: false,
                        inCall: false,
                    });
                });
            });
            this.wsClient.callTimer(false);
        }
        else {
            this.connected = false;
            this.localAudio?.close();
            this.localVideo?.close();
        }
    }
    async publish() {
        try {
            await this.client.publish([this.localAudio, this.localVideo]);
            console.log('publish success');
            this.wsClient.sendSocketMessage(this.channelId, {
                streamPublished: true,
            });
        }
        catch (e) {
            console.error('Publish local stream error: ' + e);
        }
    }
    unpublish() {
        this.client.unpublish([this.localAudio, this.localVideo]).catch(error => {
            console.error(error);
        });
        this.wsClient.sendSocketMessage(this.channelId, {
            streamPublished: false,
        });
    }
    getLocalAvalaible() {
        return this.localAvalaible$;
    }
    setLocalAvalaible(status) {
        if (this.localAvalaible.initial === null) {
            this.localAvalaible.initial = status;
        }
        this.localAvalaible.final = status;
        this.localAvalaible$.next(this.localAvalaible);
    }
    loadCameras() {
        AgoraRTC.getCameras().then((devices) => {
            const videoDevices = devices.filter(device => {
                return device.deviceId !== 'default';
            });
            this.videoDevices$.next(videoDevices);
        });
    }
    getCameras() {
        return this.videoDevices$;
    }
    loadRecordingDevices() {
        AgoraRTC.getMicrophones().then((devices) => {
            const audioDevices = devices.filter(device => {
                return device.deviceId !== 'default';
            });
            this.audioDevices$.next(audioDevices);
        });
    }
    getRecordingDevices() {
        return this.audioDevices$;
    }
    loadPlayoutDevices() {
        AgoraRTC.getPlaybackDevices().then((devices) => {
            const playoutDevices = devices.filter(device => {
                return device.deviceId !== 'default';
            });
            this.playoutDevices$.next(playoutDevices);
            if (!this.activeDevices.playout) {
                this.setActiveDevices(null, null, devices[0]);
            }
        });
    }
    getPlayoutDevices() {
        return this.playoutDevices$;
    }
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async switchCamera(camera) {
        await this.localVideo.setDevice(camera.deviceId);
        this.setActiveDevices(camera.label);
    }
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async switchAudio(audio) {
        await this.localAudio.setDevice(audio.deviceId);
        this.setActiveDevices(null, audio.label);
    }
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async switchPlayoutDevice(device) {
        await this.remoteUser?.audioTrack?.setPlaybackDevice(device.deviceId);
        this.setActiveDevices(null, null, device);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setActiveDevices(camera, audio, playout) {
        if (camera) {
            this.activeDevices.camera = camera;
        }
        if (audio) {
            this.activeDevices.audio = audio;
        }
        if (playout) {
            this.activeDevices.playout = playout;
        }
        this.activeDevices$.next(this.activeDevices);
    }
    // @TODO: Ignored with eslint-interactive on 2023-10-27
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getActiveDevices() {
        return this.activeDevices$;
    }
    // public getAudioVolume() {
    //     return this.audioVolume$;
    // }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    async setAudioVolume(setTo = null) {
        if (setTo !== null) {
            this.setVolumeTo = setTo;
        }
        else {
            if (this.audioVolume > 50) {
                this.setVolumeTo = 50;
            }
            else if (this.audioVolume > 0) {
                this.setVolumeTo = 0;
            }
            else if (this.audioVolume === 0) {
                this.setVolumeTo = 100;
            }
        }
        this.audioVolume = this.setVolumeTo;
        if (this.remoteUser) {
            this.remoteUser?.audioTrack?.setVolume(this.setVolumeTo);
        }
        this.wsClient.sendSocketMessage(this.channelId, {
            playoutMuted: this.setVolumeTo <= 0,
        });
        this.audioVolume$.next(setTo);
    }
    async toggleAudio(forceTo) {
        if (!this.localAudioEnabled && this.localAudioEnabled !== forceTo) {
            await this.localAudio.setMuted(false);
            this.localAudioEnabled = true;
        }
        else if (this.localAudioEnabled !== forceTo) {
            await this.localAudio.setMuted(true);
            this.localAudioEnabled = false;
        }
        this.wsClient.sendSocketMessage(this.channelId, {
            microphoneMuted: !this.localAudioEnabled,
        });
    }
    async toggleVideo(forceTo) {
        if (!this.localVideoEnabled && this.localVideoEnabled !== forceTo) {
            await this.localVideo.setMuted(false);
            this.localVideoEnabled = true;
        }
        else if (this.localVideoEnabled !== forceTo) {
            await this.localVideo.setMuted(true);
            this.localVideoEnabled = false;
        }
        console.log('cameraMuted', !this.localVideoEnabled);
        this.wsClient.sendSocketMessage(this.channelId, {
            cameraMuted: !this.localVideoEnabled,
        });
    }
    getProfile(profileId) {
        let profile = '720p';
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (this.streamProfiles[profileId]) {
            profile = profileId;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return this.streamProfiles[profile];
    }
    closeLocalStream() {
        this.localStream?.close();
        // @TODO: Ignored with eslint-interactive on 2023-10-27
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.client?.leave().then(r => { });
        this.localAudio?.close();
        this.localVideo?.close();
        this.telemedInitialised = false;
    }
}
AgoraService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AgoraService, deps: [{ token: 'AGORA_CONFIG' }, { token: 'LOGIN_INFO' }, { token: WebsocketClientService }, { token: TelemedicineService }], target: i0.ɵɵFactoryTarget.Injectable });
AgoraService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AgoraService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AgoraService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['AGORA_CONFIG']
                }] }, { type: i2$1.Observable, decorators: [{
                    type: Inject,
                    args: ['LOGIN_INFO']
                }] }, { type: WebsocketClientService }, { type: TelemedicineService }]; } });

/**
 * Environment Injection
 */
const ENVIRONMENT = new InjectionToken('ENVIRONMENT');
/**
 * Login info Injection
 */
const LOGIN_INFO = new InjectionToken('LOGIN_INFO');
/**
 * Agora Module
 */
class AgoraModule {
    static forRoot(agoraConfig, loginInfo, environment) {
        return {
            ngModule: AgoraModule,
            providers: [
                AgoraService,
                { provide: 'AGORA_CONFIG', useValue: agoraConfig },
                { provide: 'LOGIN_INFO', useValue: loginInfo },
                { provide: 'ENVIRONMENT', useValue: environment },
            ],
        };
    }
}
AgoraModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AgoraModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AgoraModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AgoraModule });
AgoraModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AgoraModule, imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AgoraModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: [],
                }]
        }] });

var VendorType;
(function (VendorType) {
    VendorType["INFONET"] = "infonet";
    VendorType["LIST"] = "list";
    VendorType["AUDAX"] = "audax";
    VendorType["VIZIJA"] = "vizija";
})(VendorType || (VendorType = {}));

/**
 * Websocket Chat
 */
class WebSocketChat {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    deserialize(input) {
        this.eventTime = input.eventTime;
        this.message = input.message;
        this.chat = input.chat;
        this.source = input.source;
        this.document = input.document;
        this.username = input.username;
        this.userid = input.userid;
        return this;
    }
}

/**
 * Helpers for Modals
 */
class MdsModalHelper {
    /**
     * This helper transforms MdsModal return to error if modal is dismissed or denied
     *
     * @param type Define which outcoumes do you want to transform
     * @returns Observable with users response or error
     *
     * @example
     * export class ExampleComponent {
     *   constructor(
     *     private mdsModal: MdsModalService,
     *   ) {}
     *   showModal() {
     *     this.mdsModal.showQuestionModal('Title', 'Content')
     *       .pipe(
     *         MdsModalHelper.mapSwalToError(),
     *         catchError(e => {
     *           //Do something in case modal was dismissed or denied
     *           return of(e)
     *         })
     *       )
     *       .subscribe((result) => {
     *         consol.log(result);
     *       });
     *   }
     * }
     */
    static mapSwalToError(type = ['denied', 'dismissed']) {
        return source$ => source$.pipe(map(value => {
            if (value.isDenied && type.includes('denied')) {
                throw new Error(`Modal denied by user`);
            }
            if (value.isDismissed && type.includes('dismissed')) {
                throw new Error(`Modal dismissed by user. Reason: ${value.dismiss}`);
            }
            return value;
        }));
    }
}

/**
 * Modal service. Import it in your app. In for root you can overwrite default configs
 *
 * @example
 * MdsModalModule.forRoot({
 *     default: {
 *         confirmButtonText: $localize`Potrdi`,
 *         cancelButtonText: $localize`Zapri`,
 *         denyButtonText: $localize`Prekliči`,
 *     },
 *     error: {
 *         title: $localize`Napaka`,
 *     },
 * })
 */
class MdsModalModule {
    /**
     * @param config SweetAlertOptions for each type of modal
     */
    static forRoot(config) {
        return {
            ngModule: MdsModalModule,
            providers: [{ provide: 'CONFIG_OVERRIDES', useValue: config }],
        };
    }
}
MdsModalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MdsModalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsModalModule });
MdsModalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsModalModule, providers: [], imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsModalModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    exports: [],
                    imports: [],
                    providers: [],
                }]
        }] });

var _a;
/**
 * @ignore
 */
class MdsModalConstants {
}
_a = MdsModalConstants;
MdsModalConstants.SWAL_DEFAULT = {
    icon: 'info',
    showCancelButton: true,
    showConfirmButton: false,
    showDenyButton: false,
    cancelButtonText: `Zapri`,
    confirmButtonText: `Potrdi`,
    denyButtonText: 'Prekliči',
    buttonsStyling: false,
    customClass: {
        confirmButton: 'btn btn-success m-btn m-btn--custom m-btn--pill',
        cancelButton: 'btn btn-secondary m-btn m-btn--custom m-btn--pill',
        denyButton: 'btn btn-danger m-btn m-btn--custom m-btn--pill',
    },
};
MdsModalConstants.SWAL_ERROR = {
    ..._a.SWAL_DEFAULT,
    ...{
        title: `Napaka`,
        icon: 'error',
    },
};
MdsModalConstants.SWAL_SUCCESS = {
    ..._a.SWAL_DEFAULT,
    ...{
        icon: 'success',
    },
};
MdsModalConstants.SWAL_QUESTION = {
    ..._a.SWAL_DEFAULT,
    ...{
        icon: 'question',
        showConfirmButton: true,
        showDenyButton: true,
        showCancelButton: false,
    },
};

/**
 * Service for displaying sweetaler2 modals in app.
 *
 * <example-url>/demo/mds-modal.service.html</example-url>
 */
class MdsModalService {
    /**
     * @ignore
     */
    constructor(componentFactoryResolver, applicationRef, injector, configOverrides) {
        this.componentFactoryResolver = componentFactoryResolver;
        this.applicationRef = applicationRef;
        this.injector = injector;
        this.configOverrides = configOverrides;
        /**
         * @ignore
         */
        this.swalQueue = new ReplaySubject(500);
        this.handleQueue().subscribe();
    }
    processPre(result, pre, preOptions) {
        return of(result).pipe(mergeMap(res => {
            if (!pre) {
                return of(res);
            }
            return pre(res);
        }), mergeMap(res => {
            if (isObservable(preOptions)) {
                return preOptions(res);
            }
            else if (preOptions) {
                return of(preOptions(res));
            }
            return of(res);
        }));
    }
    /**
     * @ignore
     */
    handleQueue() {
        return this.swalQueue.pipe(concatMap(({ swalResult, options }) => {
            let html;
            const content = options.html;
            let componentRef = null;
            if (!content) {
                html = '';
            }
            else if (typeof content === 'string') {
                html = content;
            }
            else {
                let component;
                let inputs;
                if (typeof content === 'object') {
                    component = content.component;
                    inputs = content.inputs;
                }
                else {
                    component = content;
                }
                componentRef = this.componentFactoryResolver.resolveComponentFactory(component).create(this.injector);
                if (inputs) {
                    componentRef.instance.inputs = inputs;
                }
                this.applicationRef.attachView(componentRef.hostView);
                html = componentRef.hostView.rootNodes[0];
            }
            let _preConfirmFn = null;
            let _preDenyFn = null;
            const swalInstance = swal.mixin(_.merge({}, options, {
                html: html,
                preConfirm: (result) => {
                    return this.processPre(result, _preConfirmFn, options.preConfirm);
                },
                preDeny: (result) => {
                    return this.processPre(result, _preDenyFn, options.preDeny).pipe(map(res => {
                        return res === false ? undefined : res;
                    }));
                },
            }));
            if (componentRef) {
                componentRef.instance.modalInstance = {
                    instance: swalInstance,
                    confirm: result => {
                        if (swalInstance) {
                            this.processPre(result, _preConfirmFn, options.preConfirm).subscribe(res => {
                                if (res) {
                                    swalInstance.close({
                                        value: res,
                                        isConfirmed: true,
                                    });
                                }
                            });
                        }
                    },
                    deny: result => {
                        if (swalInstance) {
                            this.processPre(result, _preDenyFn, options.preDeny).subscribe(res => {
                                if (res) {
                                    swalInstance.close({
                                        value: res,
                                        isDenied: true,
                                    });
                                }
                            });
                        }
                    },
                    cancel: () => {
                        if (swalInstance) {
                            swalInstance.close({
                                dismiss: swal.DismissReason.cancel,
                                isDismissed: true,
                            });
                        }
                    },
                    preConfirm: callback => (_preConfirmFn = callback),
                    preDeny: callback => (_preDenyFn = callback),
                    /*
                    Shorthands
                    */
                    showValidationMessage: (message) => swalInstance.showValidationMessage(message),
                    resetValidationMessage: () => swalInstance.resetValidationMessage(),
                };
            }
            return from(swalInstance.fire()).pipe(tap(val => {
                swalResult.next(val);
                swalResult.complete();
            }));
        }));
    }
    /**
     * @ignore
     */
    getModalSettings(title, content = null, type = 'info', settings) {
        let defOptions = _.merge({}, MdsModalConstants.SWAL_DEFAULT, this.configOverrides.default);
        switch (type) {
            case 'error':
                defOptions = _.merge({}, defOptions, MdsModalConstants.SWAL_ERROR, this.configOverrides.default, this.configOverrides.error);
                break;
            case 'success':
                defOptions = _.merge({}, defOptions, MdsModalConstants.SWAL_SUCCESS, this.configOverrides.default, this.configOverrides.success);
                break;
            case 'question':
                defOptions = _.merge({}, defOptions, MdsModalConstants.SWAL_QUESTION, this.configOverrides.default, this.configOverrides.question);
                break;
        }
        if (settings?.confirmButtonText && settings.showConfirmButton !== false) {
            settings.showConfirmButton = true;
        }
        if (settings?.cancelButtonText && settings.showCancelButton !== false) {
            settings.showCancelButton = true;
        }
        if (settings?.denyButtonText && settings.showDenyButton !== false) {
            settings.showDenyButton = true;
        }
        return _.merge({}, defOptions, settings, {
            title: title || defOptions.title,
            html: content || defOptions.text,
            icon: type,
        });
    }
    /**
     * Show Modal.
     *
     * @param title Modal title
     * @param content Accepts string or Angular component. In that case components must implement SwalContentComponent
     * @param settings Overwrite options for this modal
     * @returns Observable with users response
     *
     * @example
     * export class ExampleComponent {
     *   constructor(
     *     private mdsModal: MdsModalService,
     *   ) {}
     *   showModal() {
     *     this.mdsModal.showModal('Title', 'Content', 'info').subscribe((result) => {
     *       consol.log(result);
     *     });
     *   }
     * }
     */
    showModal(title, content, type = 'info', settings) {
        const defOptions = this.getModalSettings(title, content, type, settings);
        const swalResponse = new Subject();
        this.swalQueue.next({
            swalResult: swalResponse,
            options: defOptions,
        });
        return swalResponse;
    }
    /**
     * Show Error Modal.
     *
     * This is shorthand for this.mdsModal.showModal('Title', 'Content', 'error');
     *
     *
     * @param title Modal title
     * @param content Accepts string or Angular component. In that case components must implement SwalContentComponent
     * @param settings Overwrite options for this modal
     * @returns Observable with users response
     *
     * @example
     * export class ExampleComponent {
     *   constructor(
     *     private mdsModal: MdsModalService,
     *   ) {}
     *   showModal() {
     *     this.mdsModal.showErrorModal('Title', 'Content').subscribe((result) => {
     *       consol.log(result);
     *     });
     *   }
     * }
     */
    showErrorModal(title, content, settings) {
        return this.showModal(title, content, 'error', settings);
    }
    /**
     * Show Info Modal.
     *
     * This is shorthand for this.mdsModal.showModal('Title', 'Content', 'info');
     *
     *
     * @param title Modal title
     * @param content Accepts string or Angular component. In that case components must implement SwalContentComponent
     * @param settings Overwrite options for this modal
     * @returns Observable with users response
     *
     * @example
     * export class ExampleComponent {
     *   constructor(
     *     private mdsModal: MdsModalService,
     *   ) {}
     *   showModal() {
     *     this.mdsModal.showInfoModal('Title', 'Content').subscribe((result) => {
     *       consol.log(result);
     *     });
     *   }
     * }
     */
    showInfoModal(title, content, settings) {
        return this.showModal(title, content, 'info', settings);
    }
    /**
     * Show Success Modal.
     *
     * This is shorthand for this.mdsModal.showModal('Title', 'Content', 'success');
     *
     *
     * @param title Modal title
     * @param content Accepts string or Angular component. In that case components must implement SwalContentComponent
     * @param settings Overwrite options for this modal
     * @returns Observable with users response
     *
     * @example
     * export class ExampleComponent {
     *   constructor(
     *     private mdsModal: MdsModalService,
     *   ) {}
     *   showModal() {
     *     this.mdsModal.showSuccessModal('Title', 'Content').subscribe((result) => {
     *       consol.log(result);
     *     });
     *   }
     * }
     */
    showSuccessModal(title, content, settings) {
        return this.showModal(title, content, 'success', settings);
    }
    /**
     * Show Warning Modal.
     *
     * This is shorthand for this.mdsModal.showModal('Title', 'Content', 'warning');
     *
     *
     * @param title Modal title
     * @param content Accepts string or Angular component. In that case components must implement SwalContentComponent
     * @param settings Overwrite options for this modal
     * @returns Observable with users response
     *
     * @example
     * export class ExampleComponent {
     *   constructor(
     *     private mdsModal: MdsModalService,
     *   ) {}
     *   showModal() {
     *     this.mdsModal.showWarningModal('Title', 'Content').subscribe((result) => {
     *       consol.log(result);
     *     });
     *   }
     * }
     */
    showWarningModal(title, content, settings) {
        return this.showModal(title, content, 'warning', settings);
    }
    /**
     * Show Question Modal. By default it will have 'Confirm' and 'Deny' buttons.
     *
     * This is shorthand for this.mdsModal.showModal('Title', 'Content', 'question');
     *
     *
     * @param title Modal title
     * @param content Accepts string or Angular component. In that case components must implement SwalContentComponent
     * @param settings Overwrite options for this modal
     * @returns Observable with users response
     *
     * @example
     * export class ExampleComponent {
     *   constructor(
     *     private mdsModal: MdsModalService,
     *   ) {}
     *   showModal() {
     *     this.mdsModal.showQuestionModal('Title', 'Content').subscribe((result) => {
     *       consol.log(result);
     *     });
     *   }
     * }
     */
    showQuestionModal(title, content, settings) {
        return this.showModal(title, content, 'question', settings);
    }
}
MdsModalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsModalService, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.ApplicationRef }, { token: i0.Injector }, { token: 'CONFIG_OVERRIDES' }], target: i0.ɵɵFactoryTarget.Injectable });
MdsModalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsModalService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsModalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }, { type: i0.ApplicationRef }, { type: i0.Injector }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['CONFIG_OVERRIDES']
                }] }]; } });

class MetabaseAnalyticsConstants {
}
// @TODO Implement environment-config loader and load this endpoint from there
/** Where to get Metabase Analytics definitions */
MetabaseAnalyticsConstants.METABASE_ANALYTICS_DASHBOARD_URL = 'https://services.medifit.si/public/asistent-dashboard-analytics/';
/** Where to get Metabase dashboards/views */
MetabaseAnalyticsConstants.METABASE_URL = 'https://metabase.medifit.si/public';

/**
 * Displays iFrame with Metabase dashboard/view
 */
class MetabaseAnalyticsComponent {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
        this.iframeHeightOffset = null;
        this.view = null;
        this.loading = new BehaviorSubject(false);
        this.onDestroy$ = new Subject();
        this.iframeBase = MetabaseAnalyticsConstants.METABASE_URL;
        this.iframeUrl = null;
        this.lastIframeUrl = null;
        this.defaultIframeBaseUrl = '/dashboard/';
        /*
        Fallback if something goes wrong with onIframeLoaded()
        https://stackoverflow.com/a/38459639
       */
        this.loading
            .pipe(takeUntil(this.onDestroy$), debounceTime(10000), filter(value => value))
            .subscribe(() => {
            this.loading.next(false);
        });
    }
    get valueAsStyle() {
        if (this.iframeHeightOffset) {
            return this.sanitizer.bypassSecurityTrustStyle(`--iframe-offset: ${this.iframeHeightOffset}px`);
        }
    }
    ngOnChanges(changes) {
        this.iframeUrl = null;
        if (changes['view'] && changes['view'].currentValue) {
            this.view = changes['view'].currentValue;
            if (this.view) {
                if (this.view.iframeOffset !== false) {
                    this.view.iframeOffset = true;
                }
                this.rebuildUrl();
            }
        }
    }
    ngOnDestroy() {
        this.onDestroy$.next();
    }
    rebuildUrl() {
        if (!this.view) {
            return;
        }
        let url = `${this.iframeBase}${this.view.iframeBaseUrl ? this.view.iframeBaseUrl : this.defaultIframeBaseUrl}${this.view.guid}?`;
        //url += '#titled=false&hide_parameters=date';
        url += '#titled=false';
        this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        if (this.iframeUrl !== this.lastIframeUrl) {
            this.loading.next(true);
        }
        this.lastIframeUrl = this.iframeUrl;
    }
    print() {
        window.print();
    }
    onIframeLoaded(ev) {
        if (ev.target?.src) {
            this.loading.next(false);
        }
    }
}
MetabaseAnalyticsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MetabaseAnalyticsComponent, deps: [{ token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component });
MetabaseAnalyticsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: MetabaseAnalyticsComponent, selector: "mds-metabase-report", inputs: { iframeHeightOffset: "iframeHeightOffset", view: "view" }, outputs: { loading: "loading" }, host: { properties: { "attr.style": "this.valueAsStyle" } }, usesOnChanges: true, ngImport: i0, template: "<div *ngIf=\"view\" class=\"inner-wrapper\">\n    <div class=\"iframe-wrapper\" [ngClass]=\"{ 'iframe-offset': view.iframeOffset }\">\n        <iframe *ngIf=\"iframeUrl\" [src]=\"iframeUrl\" (load)=\"onIframeLoaded($event)\" frameborder=\"0\" id=\"metabase-iframe\"></iframe>\n    </div>\n</div>\n", styles: [".inner-wrapper{overflow:hidden}.iframe-wrapper iframe{width:100%;height:calc(100vh - var(--iframe-offset, 110px))}@media print{.iframe-wrapper iframe,.iframe-wrapper.has-filters iframe,.iframe-wrapper.iframe-offset iframe{height:var(--defaultIframePrintHeight)}}\n"], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MetabaseAnalyticsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mds-metabase-report', template: "<div *ngIf=\"view\" class=\"inner-wrapper\">\n    <div class=\"iframe-wrapper\" [ngClass]=\"{ 'iframe-offset': view.iframeOffset }\">\n        <iframe *ngIf=\"iframeUrl\" [src]=\"iframeUrl\" (load)=\"onIframeLoaded($event)\" frameborder=\"0\" id=\"metabase-iframe\"></iframe>\n    </div>\n</div>\n", styles: [".inner-wrapper{overflow:hidden}.iframe-wrapper iframe{width:100%;height:calc(100vh - var(--iframe-offset, 110px))}@media print{.iframe-wrapper iframe,.iframe-wrapper.has-filters iframe,.iframe-wrapper.iframe-offset iframe{height:var(--defaultIframePrintHeight)}}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$2.DomSanitizer }]; }, propDecorators: { iframeHeightOffset: [{
                type: Input
            }], view: [{
                type: Input
            }], loading: [{
                type: Output
            }], valueAsStyle: [{
                type: HostBinding,
                args: ['attr.style']
            }] } });

class MetabaseAnalyticsModule {
}
MetabaseAnalyticsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MetabaseAnalyticsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MetabaseAnalyticsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MetabaseAnalyticsModule, declarations: [MetabaseAnalyticsComponent], imports: [CommonModule], exports: [MetabaseAnalyticsComponent] });
MetabaseAnalyticsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MetabaseAnalyticsModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MetabaseAnalyticsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MetabaseAnalyticsComponent],
                    exports: [MetabaseAnalyticsComponent],
                    imports: [CommonModule],
                }]
        }] });

class MetabaseAnalyticsService {
    constructor(httpClient) {
        this.httpClient = httpClient;
        this.METABASE_ANALYTICS_DASHBOARD_URL = MetabaseAnalyticsConstants.METABASE_ANALYTICS_DASHBOARD_URL;
    }
    /**
     * Returns analytics settings
     *
     * @param bsId Business subject ID
     * @param userGuid User token (a_token)
     */
    getAnalyticsConfig(bsId, userGuid) {
        return this.httpClient
            .get(this.METABASE_ANALYTICS_DASHBOARD_URL, {
            params: {
                bsid: bsId,
                user: userGuid,
            },
        })
            .pipe(map(response => response), shareReplay({ bufferSize: 1, refCount: true }));
    }
    /**
     *
     * @param view Analytics view to download
     */
    downloadAnalytics(view) {
        window.open(`${MetabaseAnalyticsConstants.METABASE_URL}${view.iframeBaseUrl}${view.guid}.${view.downloadType}`, '_blank');
    }
}
MetabaseAnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MetabaseAnalyticsService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
MetabaseAnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MetabaseAnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MetabaseAnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

class MfCaptchaModule {
    static forRoot(config) {
        return {
            ngModule: MfCaptchaModule,
            providers: [
                { provide: RECAPTCHA_LANGUAGE, useValue: config.language || 'en' },
                { provide: RECAPTCHA_V3_SITE_KEY, useValue: config.siteKey }
            ]
        };
    }
}
MfCaptchaModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MfCaptchaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MfCaptchaModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MfCaptchaModule, imports: [CommonModule,
        RecaptchaModule,
        RecaptchaV3Module] });
MfCaptchaModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MfCaptchaModule, imports: [[
            CommonModule,
            RecaptchaModule,
            RecaptchaV3Module,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MfCaptchaModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        CommonModule,
                        RecaptchaModule,
                        RecaptchaV3Module,
                    ]
                }]
        }] });

class MfCaptchaService {
    constructor(recaptchaV3Service) {
        this.recaptchaV3Service = recaptchaV3Service;
    }
    post(httpClient, captchaAction, url, body = {}, options = {}) {
        return this.recaptchaV3Service.execute(captchaAction).pipe(switchMap((recaptchaToken) => {
            let params = new HttpParams();
            if (options && options.params instanceof HttpParams) {
                params = options.params;
            }
            params = params.append('grecaptcha', recaptchaToken);
            return httpClient.post(url, body, { ...options, params });
        }));
    }
}
MfCaptchaService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MfCaptchaService, deps: [{ token: i1$3.ReCaptchaV3Service }], target: i0.ɵɵFactoryTarget.Injectable });
MfCaptchaService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MfCaptchaService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MfCaptchaService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$3.ReCaptchaV3Service }]; } });

/**
 * @ignore
 */
class MdsDefaultComponent {
}
MdsDefaultComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsDefaultComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MdsDefaultComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: MdsDefaultComponent, selector: "mds-default", ngImport: i0, template: `
        <p>This is default component. It does nothing!</p>
    `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsDefaultComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'mds-default',
                    template: `
        <p>This is default component. It does nothing!</p>
    `,
                    styles: [],
                }]
        }] });

class MdsDefaultModule {
}
MdsDefaultModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsDefaultModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MdsDefaultModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsDefaultModule, declarations: [MdsDefaultComponent], exports: [MdsDefaultComponent] });
MdsDefaultModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsDefaultModule, providers: [], imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsDefaultModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MdsDefaultComponent],
                    imports: [],
                    exports: [MdsDefaultComponent],
                    providers: [],
                }]
        }] });

/**
 * @ignore
 */
class MdsDefaultService {
    defaultFunction(text) {
        console.log(`MdsDefaultService's defaultFunction was called with 3 ${text}`);
        return text;
    }
}
MdsDefaultService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsDefaultService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
MdsDefaultService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsDefaultService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MdsDefaultService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/*
 * Public API Surface of fe-shared
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AgoraModule, AgoraService, AnimationDefinitions, AppBasicsModule, AppBasicsService, AppInfoComponent, AppInfoService, ArrayContainsPipe, BugsnagService, ENVIRONMENT, HttpClientService, LOGIN_INFO, LocalAvalaibleEnum, MdsDefaultComponent, MdsDefaultModule, MdsDefaultService, MdsModalHelper, MdsModalModule, MdsModalService, MetabaseAnalyticsComponent, MetabaseAnalyticsModule, MetabaseAnalyticsService, MfCaptchaModule, MfCaptchaService, ObjectState, RxWebsocketSubject, StorageService, TelemedicineService, VendorType, WebSocketChannelUser, WebSocketChat, WebsocketClientService, WsCallStateType };
